<template>
  <!-- O scroll fica num contentor em bloco: numa coluna flex com altura fixa,
       as tabelas (overflow: hidden) encolhiam para caber e ficavam cortadas. -->
  <div class="h-full w-full max-w-full overflow-y-auto scrollbar-thin scrollbar-tema text-[var(--text)]">
  <div class="flex flex-col gap-3 pb-2">
    <div v-if="loading" class="text-center py-10">
      <i class="fa-solid fa-spinner fa-spin text-2xl"></i>
      <p class="mt-2">A carregar contrato...</p>
    </div>

    <div v-else-if="!detalhe" class="text-center py-10">
      <p>Contrato não encontrado.</p>
      <button class="mt-3 underline" @click="voltar">Voltar aos contratos</button>
    </div>

    <template v-else>
      <!-- Cabeçalho -->
      <div class="flex flex-wrap items-start gap-3">
        <button class="mt-1" title="Voltar aos contratos" @click="voltar">
          <i class="fa-solid fa-arrow-left text-xl"></i>
        </button>
        <div class="flex-1 min-w-0">
          <h2 class="text-[#ff9b05] text-xl font-semibold break-words">{{ c.nome }}</h2>
          <p class="opacity-80">{{ c.cliente_nome }} › {{ c.subcliente_nome }}</p>
        </div>
        <div class="flex flex-col items-end gap-1">
          <span class="px-2 py-0.5 rounded text-sm" :class="classeEstado">{{ c.estado }}</span>
          <span class="text-sm opacity-80">{{ textoPrazo }}</span>
        </div>
      </div>

      <!-- Dados do contrato + documentos -->
      <section class="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div class="lg:col-span-2 rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
          <h3 class="text-[#ff9b05] font-semibold mb-2">Dados do contrato</h3>
          <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt class="opacity-70">Tipo</dt><dd>{{ c.tipo_contrato_nome || "—" }}</dd>
            <dt class="opacity-70">Tabela de preços</dt><dd>{{ c.precario_nome || "—" }}</dd>
            <dt class="opacity-70">Período</dt>
            <dd>{{ formatarData(c.data_inicio) }} a {{ formatarData(c.data_validade) }} ({{ c.duracao_dias }} dias)</dd>
            <dt class="opacity-70">Famílias</dt><dd>{{ c.familias.join(", ") || "—" }}</dd>
            <dt class="opacity-70">Instalações</dt><dd>{{ c.n_instalacoes }}</dd>
            <dt class="opacity-70">Aviso de término</dt>
            <dd>{{ c.dias_notificacao === null ? "—" : `${c.dias_notificacao} dias antes do fim` }}</dd>
            <dt class="opacity-70">Observações</dt>
            <dd class="whitespace-pre-wrap break-words">{{ c.observacoes || "—" }}</dd>
          </dl>
        </div>
        <div class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
          <h3 class="text-[#ff9b05] font-semibold mb-2">Documentos</h3>
          <p v-if="!detalhe.documentos.length" class="text-sm opacity-70">Sem documentos</p>
          <ul v-else class="flex flex-col gap-1">
            <li
              v-for="doc in detalhe.documentos"
              :key="doc.id"
              class="flex items-center gap-2 text-sm bg-black/20 rounded-[5px] px-2 py-1"
            >
              <i :class="iconeFicheiro(doc.extensao)"></i>
              <button
                class="flex-1 min-w-0 truncate text-left hover:underline"
                :title="doc.nome_original"
                @click="abrirDocumentoContrato(doc)"
              >{{ doc.nome_original }}</button>
              <span class="text-xs opacity-70 whitespace-nowrap">{{ formatarTamanho(doc.tamanho) }}</span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Valores -->
      <section class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
        <h3 class="text-[#ff9b05] font-semibold mb-2">Valores</h3>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <div v-for="v in valores" :key="v.label" class="rounded-md p-2 bg-black/20 border border-[var(--glass-border)] flex flex-col">
            <span class="text-xs opacity-70">{{ v.label }}</span>
            <span class="text-lg font-semibold">{{ v.valor === null ? "—" : formatarValor(v.valor) }}</span>
          </div>
        </div>
        <div v-if="detalhe.faturacao.percentagem_valor_global !== null" class="mt-3">
          <div class="flex justify-between text-xs opacity-80 mb-1">
            <span>Faturado face ao valor global</span>
            <span>{{ detalhe.faturacao.percentagem_valor_global }}%</span>
          </div>
          <div class="h-2 rounded bg-black/30 overflow-hidden">
            <div
              class="h-full"
              :class="detalhe.faturacao.percentagem_valor_global > 100 ? 'bg-red-500' : 'bg-[#ff9b05]'"
              :style="{ width: Math.min(detalhe.faturacao.percentagem_valor_global, 100) + '%' }"
            ></div>
          </div>
        </div>
      </section>

      <!-- OS e intervenções -->
      <section class="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div v-for="bloco in blocosOS" :key="bloco.titulo" class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
          <h3 class="text-[#ff9b05] font-semibold mb-2">{{ bloco.titulo }}</h3>
          <dl class="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
            <dt class="opacity-70">Total</dt><dd class="text-right font-semibold">{{ bloco.dados.total }}</dd>
            <dt class="opacity-70">Realizadas</dt><dd class="text-right">{{ bloco.dados.realizadas }}</dd>
            <dt class="opacity-70">Em curso</dt><dd class="text-right">{{ bloco.dados.em_curso }}</dd>
            <dt class="opacity-70">Canceladas</dt><dd class="text-right">{{ bloco.dados.canceladas }}</dd>
            <dt class="opacity-70">Faturado</dt><dd class="text-right">{{ formatarValor(bloco.dados.faturado) }}</dd>
          </dl>
        </div>
        <div class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
          <h3 class="text-[#ff9b05] font-semibold mb-2">Intervenções</h3>
          <dl class="grid grid-cols-[1fr_auto] gap-y-1 text-sm">
            <dt class="opacity-70">Instalações intervencionadas</dt>
            <dd class="text-right">{{ i.instalacoes_intervencionadas }} / {{ i.instalacoes_total }}</dd>
            <dt class="opacity-70">Equipamentos intervencionados</dt>
            <dd class="text-right">{{ i.equipamentos_intervencionados }}</dd>
            <dt class="opacity-70">Manutenções</dt><dd class="text-right">{{ i.manutencoes }}</dd>
            <dt class="opacity-70">Recargas</dt><dd class="text-right">{{ i.recargas }}</dd>
            <dt class="opacity-70">Provas hidráulicas</dt><dd class="text-right">{{ i.provas_hidraulicas }}</dd>
            <dt class="opacity-70">Testes hidrostáticos</dt><dd class="text-right">{{ i.testes_hidrostaticos }}</dd>
          </dl>
        </div>
      </section>

      <!-- Estimativa pela periodicidade -->
      <section class="rounded-lg p-3 bg-black/20 border border-[var(--glass-border)]">
        <h3 class="text-[#ff9b05] font-semibold">Estimativa de OS preventivas pela periodicidade</h3>
        <p class="text-xs opacity-70 mb-2">
          Por instalação e família com equipamentos instalados: duração do contrato ÷ periodicidade de
          manutenção (da instalação ou, se não tiver, da família), com pelo menos uma OS.
          <template v-if="e.sem_equipamentos">
            {{ e.sem_equipamentos }} combinações instalação/família do contrato não têm equipamentos nem OS e não contam.
          </template>
          <template v-if="e.sem_periodicidade">
            {{ e.sem_periodicidade }} não têm periodicidade definida.
          </template>
        </p>
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          <div v-for="k in kpisEstimativa" :key="k.label" class="rounded-md p-2 bg-black/20 border border-[var(--glass-border)] flex flex-col">
            <span class="text-xs opacity-70">{{ k.label }}</span>
            <span class="text-lg font-semibold">{{ k.valor }}</span>
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left opacity-70 border-b border-white/10">
                <th class="py-1 pr-3">Família</th>
                <th class="py-1 pr-3 text-right">Instalações</th>
                <th class="py-1 pr-3 text-right">Equip. instalados</th>
                <th class="py-1 pr-3">Periodicidade</th>
                <th class="py-1 pr-3 text-right">Previstas</th>
                <th class="py-1 pr-3 text-right">Realizadas</th>
                <th class="py-1 pr-3 text-right">Em curso</th>
                <th class="py-1 text-right">{{ rotuloPorRealizar }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in e.familias" :key="f.familia" class="border-b border-white/5">
                <td class="py-1 pr-3">{{ f.familia }}</td>
                <td class="py-1 pr-3 text-right">{{ f.instalacoes }}</td>
                <td class="py-1 pr-3 text-right">{{ f.equipamentos_instalados }}</td>
                <td class="py-1 pr-3">{{ f.periodicidades.map(textoPeriodicidade).join(" / ") || "—" }}</td>
                <td class="py-1 pr-3 text-right">{{ f.previstas }}</td>
                <td class="py-1 pr-3 text-right">{{ f.realizadas }}</td>
                <td class="py-1 pr-3 text-right">{{ f.em_curso }}</td>
                <td class="py-1 text-right font-semibold">{{ f.por_realizar }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="mt-3 text-sm underline" @click="verPorInstalacao = !verPorInstalacao">
          {{ verPorInstalacao ? "Esconder" : "Ver" }} por instalação ({{ e.instalacoes.length }})
        </button>
        <GenericDataTable
          v-if="verPorInstalacao"
          class="mt-2 border border-[var(--glass-border)]"
          :columns="colunasInstalacoes"
          :tableData="e.instalacoes"
          :useServerSide="false"
          :show_export="true"
          :show_close_btn="false"
          export_filename="estimativa_os_contrato"
          table_title="Estimativa por instalação"
        />
      </section>

      <!-- Lista de OS -->
      <GenericDataTable
        class="border border-[var(--glass-border)]"
        :columns="colunasOS"
        :tableData="detalhe.lista_os"
        :useServerSide="false"
        :show_export="true"
        :show_close_btn="false"
        export_filename="os_contrato"
        table_title="Ordens de serviço do contrato"
      />
    </template>
  </div>
  </div>
</template>

<script>
import axios from "axios";
import { toast } from "vue3-toastify";
import GenericDataTable from "@/components/dataTable/GenericDataTable2.vue";
import {
  abrirDocumentoContrato,
  iconeFicheiro,
  formatarTamanho,
} from "@/utils/documentoContrato";

// Detalhe de um contrato agrupado: dados, valores, OS, intervenções e
// estimativa de OS pela periodicidade. Dados e regras: detalhe_contrato em
// backend/api/services/contrato_resumo.py.
const ESTADO_CLASSES = {
  "Em vigor": "bg-green-600 text-white",
  Expirado: "bg-red-600 text-white",
  Futuro: "bg-blue-600 text-white",
};

export default {
  name: "appContratoDetalhe",
  components: { GenericDataTable },
  data() {
    return {
      baseURl: process.env.VUE_APP_URL_BASE,
      detalhe: null,
      loading: false,
      verPorInstalacao: false,
    };
  },
  computed: {
    c() {
      return this.detalhe.contrato;
    },
    i() {
      return this.detalhe.intervencao;
    },
    e() {
      return this.detalhe.estimativa;
    },
    classeEstado() {
      return ESTADO_CLASSES[this.c.estado] || "";
    },
    textoPrazo() {
      const dias = this.c.dias_restantes;
      if (this.c.estado === "Futuro") return `Começa a ${this.formatarData(this.c.data_inicio)}`;
      if (dias < 0) return `Expirou há ${-dias} dia${dias === -1 ? "" : "s"}`;
      if (dias === 0) return "Termina hoje";
      return `Termina em ${dias} dia${dias === 1 ? "" : "s"}`;
    },
    valores() {
      const f = this.detalhe.faturacao;
      return [
        { label: "Valor global", valor: this.c.valor_global },
        { label: "Faturas preventivas", valor: f.preventivas },
        { label: "Faturas corretivas", valor: f.corretivas },
        { label: "Total faturado", valor: f.total },
      ];
    },
    blocosOS() {
      return [
        { titulo: "OS preventivas", dados: this.detalhe.os.preventivas },
        { titulo: "OS corretivas", dados: this.detalhe.os.corretivas },
      ];
    },
    rotuloPorRealizar() {
      return this.c.estado === "Expirado" ? "Não realizadas" : "Por realizar";
    },
    kpisEstimativa() {
      return [
        { label: "Previstas no contrato", valor: this.e.previstas },
        { label: "Realizadas", valor: this.e.realizadas },
        { label: "Em curso", valor: this.e.em_curso },
        { label: this.rotuloPorRealizar, valor: this.e.por_realizar },
      ];
    },
    colunasInstalacoes() {
      return [
        { key: "instalacao", label: "Instalação", maxWidth: 220, sortable: true },
        { key: "familia", label: "Família", maxWidth: 140, sortable: true },
        { key: "equipamentos_instalados", label: "Equip. instalados", maxWidth: 90, sortable: true },
        {
          key: "periodicidade_dias",
          label: "Periodicidade",
          maxWidth: 140,
          sortable: true,
          render: (data, type, row) =>
            type === "display"
              ? data
                ? `${this.textoPeriodicidade(data)} (${row.periodicidade_origem})`
                : "—"
              : data,
        },
        { key: "previstas", label: "Previstas", maxWidth: 80, sortable: true },
        { key: "realizadas", label: "Realizadas", maxWidth: 80, sortable: true },
        { key: "em_curso", label: "Em curso", maxWidth: 80, sortable: true },
        { key: "por_realizar", label: this.rotuloPorRealizar, maxWidth: 90, sortable: true },
      ];
    },
    colunasOS() {
      return [
        { key: "id", label: "OS", maxWidth: 70, sortable: true },
        { key: "data", label: "Data", maxWidth: 100, sortable: true, type: "date" },
        { key: "instalacao", label: "Instalação", maxWidth: 220, sortable: true },
        { key: "familia", label: "Família", maxWidth: 140, sortable: true },
        { key: "tipo", label: "Tipo", maxWidth: 90, sortable: true },
        { key: "estado", label: "Estado", maxWidth: 90, sortable: true },
        {
          key: "faturado",
          label: "Faturado",
          maxWidth: 100,
          sortable: true,
          render: (data, type) => (type === "display" ? this.formatarValor(data) : data),
        },
      ];
    },
  },
  watch: {
    "$route.params.id": {
      handler() {
        this.carregar();
      },
      immediate: true,
    },
  },
  methods: {
    abrirDocumentoContrato,
    iconeFicheiro,
    formatarTamanho,
    async carregar() {
      const id = this.$route.params.id;
      if (!id) return;
      this.loading = true;
      try {
        const response = await axios.get(`${this.baseURl}/api/v1/contratos/${id}/detalhe/`);
        this.detalhe = response.data?.data || null;
      } catch (error) {
        this.detalhe = null;
        if (error.response?.status !== 404) {
          toast.error("Erro ao carregar o contrato");
        }
      } finally {
        this.loading = false;
      }
    },
    voltar() {
      this.$router.push({ name: "contratos" });
    },
    formatarValor(valor) {
      if (valor === null || valor === undefined || valor === "") return "";
      return new Intl.NumberFormat("pt-PT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(valor));
    },
    formatarData(data) {
      return data ? String(data).split("-").reverse().join("/") : "—";
    },
    textoPeriodicidade(dias) {
      const meses = Math.round(dias / 30.4);
      return meses >= 1 ? `${meses} ${meses === 1 ? "mês" : "meses"}` : `${dias} dias`;
    },
  },
};
</script>
